/**
 * PurchaseService.js — Service centralisé pour la gestion des achats (version web)
 *
 * Résout les problèmes suivants :
 * 1. Paiements perdus quand l'utilisateur ferme le navigateur → pending_payments
 * 2. Documents non disponibles après modification par l'admin → URLs temps réel
 * 3. Achats en double → vérification de doublons
 */

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  query,
  where,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../private/firebaseConfig';
import CampayService from './CampayService';
import { resolveEpreuve, readEpreuveUrl, findMatchingPurchase } from './documentIdentity';

class PurchaseService {

  // ───────────────────────────── PENDING PAYMENTS ─────────────────────────────

  /**
   * Crée un paiement en attente AVANT d'appeler CamPay.
   * Si l'utilisateur ferme le navigateur, on pourra retrouver ce paiement.
   */
  static async createPendingPayment({
    userId,
    documentId,
    documentType,
    paymentMethod,
    paymentReference,
    amount,
    metadata = {},
  }) {
    try {
      const pendingData = {
        userId,
        documentId,
        documentType,
        paymentMethod,
        paymentReference,
        amount,
        status: 'pending',
        createdAt: serverTimestamp(),
        ...metadata,
      };

      const docRef = await addDoc(collection(db, 'pending_payments'), pendingData);
      console.log('Pending payment créé:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Erreur création pending payment:', error);
      throw error;
    }
  }

  /**
   * Confirme un paiement en attente : crée le purchase + supprime le pending.
   */
  static async confirmPendingPayment(pendingPaymentId, transactionReference) {
    try {
      // 1. Lire le pending payment
      const pendingRef = doc(db, 'pending_payments', pendingPaymentId);
      const pendingSnap = await getDoc(pendingRef);

      if (!pendingSnap.exists()) {
        console.warn('Pending payment non trouvé:', pendingPaymentId);
        return null;
      }

      const pendingData = pendingSnap.data();

      // 2. Vérifier qu'un achat n'existe pas déjà
      const existingPurchase = await this.checkExistingPurchase(
        pendingData.userId,
        pendingData.documentId,
        pendingData.documentType,
        pendingData
      );

      if (existingPurchase) {
        console.log('Achat déjà existant, suppression du pending');
        await deleteDoc(pendingRef);
        return existingPurchase;
      }

      // 3. Créer le purchase
      const { status, createdAt, ...purchaseFields } = pendingData;
      const purchaseData = {
        ...purchaseFields,
        paymentReference: transactionReference || pendingData.paymentReference,
        status: 'completed',
        purchaseDate: serverTimestamp(),
      };

      const purchaseRef = await addDoc(collection(db, 'purchases'), purchaseData);
      console.log('Purchase créé depuis pending:', purchaseRef.id);

      // 4. Supprimer le pending payment
      await deleteDoc(pendingRef);

      return { id: purchaseRef.id, ...purchaseData };
    } catch (error) {
      console.error('Erreur confirmation pending payment:', error);
      throw error;
    }
  }

  /**
   * Vérifie les paiements en attente au lancement de l'app.
   * Pour chaque pending de moins d'1h, on vérifie le statut CamPay.
   */
  static async checkPendingPayments(userId) {
    try {
      const q = query(
        collection(db, 'pending_payments'),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log('Aucun paiement en attente');
        return [];
      }

      const confirmedPayments = [];
      const now = Date.now();
      // Rétention alignée sur le mobile (24h) : un paiement mobile money peut
      // aboutir bien après 1h. Supprimer trop tôt = perte d'un achat payé.
      const RETENTION_MS = 24 * 60 * 60 * 1000;

      for (const pendingDoc of snapshot.docs) {
        const data = pendingDoc.data();
        const createdAt = data.createdAt?.toDate?.()?.getTime() || 0;

        // Supprimer les pending trop anciens (> 24h)
        if (now - createdAt > RETENTION_MS) {
          console.log('Suppression pending expiré:', pendingDoc.id);
          await deleteDoc(doc(db, 'pending_payments', pendingDoc.id));
          continue;
        }

        // Vérifier le statut auprès de CamPay.
        // ⚠️ Utiliser campayReference (référence CamPay) et NON paymentReference
        // (référence interne ORDER-... que CamPay ne connaît pas).
        const campayRef = data.campayReference || null;
        if (campayRef) {
          try {
            const statusResult = await CampayService.checkPaymentStatus(campayRef);

            if (CampayService.isPaymentComplete(statusResult?.status)) {
              console.log('Paiement confirmé pour pending:', pendingDoc.id);
              const purchase = await this.confirmPendingPayment(
                pendingDoc.id,
                campayRef
              );
              if (purchase) {
                confirmedPayments.push(purchase);
              }
            } else if (CampayService.isPaymentFailed(statusResult?.status)) {
              console.log('Paiement échoué, suppression pending:', pendingDoc.id);
              await deleteDoc(doc(db, 'pending_payments', pendingDoc.id));
            }
            // Si toujours PENDING, on laisse pour la prochaine vérification
          } catch (err) {
            console.error('Erreur vérification CamPay pour pending:', pendingDoc.id, err);
          }
        }
      }

      return confirmedPayments;
    } catch (error) {
      console.error('Erreur checkPendingPayments:', error);
      return [];
    }
  }

  // ──────────────────────────── VÉRIFICATION DOUBLONS ────────────────────────

  /**
   * Vérifie si un achat existe déjà pour éviter les doublons.
   */
  static async checkExistingPurchase(userId, documentId, documentType, extra = {}) {
    try {
      // On ne filtre PLUS sur titre/annee/filiere dans la requête Firestore :
      // `where('titre','==',...)` est une égalité binaire, donc une simple
      // correction de titre par l'admin faisait échouer la détection et
      // laissait l'utilisateur payer DEUX FOIS le même document.
      const q = query(
        collection(db, 'purchases'),
        where('userId', '==', userId),
        where('documentId', '==', documentId),
        where('status', '==', 'completed')
      );
      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const achats = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Bords et documents simples : documentId identifie déjà l'achat.
      if (documentType !== 'concours') return achats[0];

      const found = findMatchingPurchase(
        achats,
        { epreuveId: extra.epreuveId, titre: extra.titre, annee: extra.annee, filiere: extra.filiere },
        { documentId, groupId: extra.groupId }
      );
      return found || null;
    } catch (error) {
      console.error('Erreur checkExistingPurchase:', error);
      return null;
    }
  }

  // ──────────────────────────── SAUVEGARDE D'ACHAT ──────────────────────────

  /**
   * Enregistre un achat directement (pour les documents gratuits par ex.).
   * Vérifie d'abord les doublons.
   */
  static async savePurchase(purchaseData) {
    try {
      // Vérifier les doublons
      const existing = await this.checkExistingPurchase(
        purchaseData.userId,
        purchaseData.documentId,
        purchaseData.documentType,
        purchaseData
      );

      if (existing) {
        console.log('Achat déjà existant, pas de doublon');
        return existing;
      }

      const docRef = await addDoc(collection(db, 'purchases'), {
        ...purchaseData,
        status: 'completed',
        purchaseDate: serverTimestamp(),
      });

      console.log('Purchase sauvegardé:', docRef.id);
      return { id: docRef.id, ...purchaseData };
    } catch (error) {
      console.error('Erreur savePurchase:', error);
      throw error;
    }
  }

  // ──────────────────────────── URLs TEMPS RÉEL ─────────────────────────────

  /**
   * Récupère l'URL actuelle d'un document depuis Firestore (pas depuis purchases).
   * C'est la clé pour résoudre le problème des URLs obsolètes après modification admin.
   */
  /**
   * Résolution en cascade identique au mobile (voir documentIdentity.js) :
   * epreuveId → texte normalisé → titre+année. Une modification admin des
   * métadonnées ne fait jamais perdre l'accès à un document déjà payé.
   *
   * @param {object} [extra]
   * @param {string} [extra.epreuveId]  Identité stable stockée dans l'achat.
   * @param {string} [extra.purchaseId] Active l'auto-réparation de l'achat.
   */
  static async getDocumentUrl(documentId, documentType, extra = {}) {
    try {
      if (documentType === 'bords') {
        const docSnap = await getDoc(doc(db, 'bords', documentId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (Array.isArray(data.documents) && data.documents.length > 0) {
            // `extra.index` cible une partie précise d'un bord multi-documents.
            // Sans lui, toutes les parties ouvraient documents[0].
            const i = Number.isInteger(extra.index) ? extra.index : 0;
            return readEpreuveUrl(data.documents[i] ?? data.documents[0]);
          }
          return readEpreuveUrl(data);
        }
        return null;
      }

      if (documentType === 'concours') {
        const docSnap = await getDoc(doc(db, 'concours', documentId));
        if (!docSnap.exists()) return null;

        const resolved = resolveEpreuve(docSnap.data(), {
          epreuveId: extra.epreuveId,
          titre: extra.titre,
          annee: extra.annee,
          filiere: extra.filiere,
          groupId: extra.groupId,
        });
        if (!resolved) return null;

        // Auto-réparation : grave l'identifiant stable dans l'achat quand la
        // résolution a dû passer par un repli textuel.
        if (resolved.strategy !== 'id' && extra.purchaseId) {
          const stableId = resolved.epreuve?.epreuveId || resolved.epreuve?.id;
          if (stableId) {
            updateDoc(doc(db, 'purchases', extra.purchaseId), {
              epreuveId: stableId,
              ...(resolved.groupId ? { groupId: resolved.groupId } : {}),
              repairedAt: serverTimestamp(),
            }).catch(() => { /* best-effort, ne bloque jamais la lecture */ });
          }
        }

        return resolved.url;
      }

      if (documentType === 'documents') {
        const docSnap = await getDoc(doc(db, 'documents', documentId));
        if (docSnap.exists()) return readEpreuveUrl(docSnap.data());
      }

      return null;
    } catch (error) {
      console.error('Erreur getDocumentUrl:', error);
      return null;
    }
  }

  /**
   * Récupère l'URL de la version anglaise d'un bord depuis Firestore.
   */
  static async getDocumentUrlAnglais(documentId) {
    try {
      const docRef = doc(db, 'bords', documentId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (Array.isArray(data.documentsAnglais) && data.documentsAnglais.length > 0) {
          return readEpreuveUrl(data.documentsAnglais[0]);
        }
      }

      return null;
    } catch (error) {
      console.error('Erreur getDocumentUrlAnglais:', error);
      return null;
    }
  }

  /**
   * Récupère l'URL courante d'un document OPTIONNEL d'un bord.
   * Résolution par `id` stable, repli par titre. Identique au mobile.
   */
  static async getDocumentUrlOptionnel(bordId, optionnel) {
    try {
      const docSnap = await getDoc(doc(db, 'bords', bordId));
      if (!docSnap.exists()) return null;

      const list = docSnap.data().documentsOptionnels;
      if (!Array.isArray(list) || list.length === 0) return null;

      if (optionnel?.id) {
        const found = list.find((o) => o?.id === optionnel.id);
        if (found) return readEpreuveUrl(found) || found.document || null;
      }
      if (optionnel?.titre) {
        const found = list.find((o) => o?.titre === optionnel.titre);
        if (found) return readEpreuveUrl(found) || found.document || null;
      }
      return null;
    } catch (error) {
      console.error('Erreur getDocumentUrlOptionnel:', error);
      return null;
    }
  }

  /**
   * Récupère les données complètes d'un document depuis Firestore.
   */
  static async getFullDocumentData(documentId, documentType) {
    try {
      const collectionName = documentType === 'concours' ? 'concours' : documentType === 'bords' ? 'bords' : 'documents';
      const docRef = doc(db, collectionName, documentId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }

      return null;
    } catch (error) {
      console.error('Erreur getFullDocumentData:', error);
      return null;
    }
  }
}

export default PurchaseService;
